import { useEffect } from 'react';
import { Redirect, Route, Switch, generatePath, useLocation, useRouteMatch } from 'react-router';
import { z } from 'zod';
import { create } from 'zustand';

import * as ROOT_PATHS from '../../../../util/pagePath';
import { SignUpPage } from './SignUpPage';
import { SignUpPasswordPage } from './SignUpPasswordPage';
import SignUpReviewDetailsPage from './SignUpReviewDetailsPage';
import { SignUpStudentDetails } from './SignUpStudentDetails';
import SignUpSuccessPage from './SignUpSuccessPage';
import { SignUpVerifyPage } from './SignUpVerifyPage';

const ROOT_PATH = ROOT_PATHS.PATH_HUB_SIGN_UP;
const VERIFY_PATH = `${ROOT_PATH}verify`;
const PASSWORD_PATH = `${ROOT_PATH}password`;
const STUDENT_DETAILS_PATH = `${ROOT_PATH}student-details`;
const REVIEW_DETAILS_PATH = `${ROOT_PATH}review-details`;
const SUCCESS_PATH = `${ROOT_PATH}success`;

export const PATHS = {
  ROOT_PATH,
  VERIFY_PATH,
  PASSWORD_PATH,
  STUDENT_DETAILS_PATH,
  REVIEW_DETAILS_PATH,
  SUCCESS_PATH,
} as const;

export const useQueryParams = () => {
  const { search } = useLocation();
  return Object.fromEntries(new URLSearchParams(search));
};

export const SignUpRouter = () => {
  const { path } = useRouteMatch();
  const { search } = useLocation();
  const queryParams = Object.fromEntries(new URLSearchParams(search));

  useEffect(() => {
    console.log('[debug] queryParams:', queryParams);
  }, [queryParams]);

  console.log(generatePath(path));

  return (
    <Switch>
      <Route path={ROOT_PATH} exact component={SignUpPage} />
      <Route path={VERIFY_PATH} exact component={SignUpVerifyPage} />
      <Route path={PASSWORD_PATH} exact component={SignUpPasswordPage} />
      <Route path={STUDENT_DETAILS_PATH} exact component={SignUpStudentDetails} />
      <Route path={REVIEW_DETAILS_PATH} exact component={SignUpReviewDetailsPage} />
      <Route path={SUCCESS_PATH} exact component={SignUpSuccessPage} />
      <Route path={`${ROOT_PATH}*`} component={() => <Redirect to={`${ROOT_PATH}${search}`} />} />
    </Switch>
  );
};

export const SIGNUP_ORIGIN = z.enum(['worksheets', 'assessments']);
export type T_SIGNUP_ORIGIN = z.infer<typeof SIGNUP_ORIGIN>;

type SignUpStore = {
  username: string;
  email: string;
  fname: string;
  lname: string;
  token: string;
  tempPwd: string;
  mobileNumber: string;
  origin: T_SIGNUP_ORIGIN | null;
  customerNumber: string;
};

type SignUpStoreModifiers = {
  setState: (state: Partial<SignUpStore>) => void;
  clearState: () => void;
};

const initialState: SignUpStore = {
  username: '',
  email: '',
  fname: '',
  lname: '',
  token: '',
  origin: null,
  tempPwd: '',
  customerNumber: '',
  mobileNumber: '',
};

export const useSignupStore = create<SignUpStore & SignUpStoreModifiers>((set) => ({
  ...initialState,
  setState: (state) => set(state),
  clearState: () =>
    set({
      ...initialState,
    }),
}));

const tokenTypeI = z.object({
  tmpPassword: z.string(),
  email: z.string(),
});
type RawTokenTypeI = z.infer<typeof tokenTypeI>;
type TokenTypeI = Required<RawTokenTypeI>;

const tokenTypeII = z.object({
  temporaryPassword: z.string(),
  email: z.string(),
});
type RawTokenTypeII = z.infer<typeof tokenTypeII>;
type TokenTypeII = Required<RawTokenTypeII>;

export const isTokenTypeI = (tokenResult: TokenTypeI | TokenTypeII): tokenResult is TokenTypeI => {
  return 'tmpPassword' in tokenResult;
};

export const tokenPassword = (tokenResult: TokenTypeI | TokenTypeII) => {
  return isTokenTypeI(tokenResult) ? tokenResult.tmpPassword : tokenResult.temporaryPassword;
};

export const decodeToken = (token: string) => {
  const decoded = JSON.parse(Buffer.from(token, 'base64').toString());

  const parse = z.union([tokenTypeI, tokenTypeII]).safeParse(decoded);

  return parse.success ? (parse.data as TokenTypeI | TokenTypeII) : null;
};
